import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, AlertOctagon, Cog, Wrench, Clock, DollarSign, TrendingUp, Settings } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const EquipmentFailures = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const failureTypes = [
    {
      title: 'Bearing Failures',
      description: 'Worn or damaged bearings causing vibration, noise, and eventual seizure.',
      causes: ['Lubrication issues', 'Misalignment', 'Overloading', 'Contamination'],
      repair: 'Bearing housing rebuilding and precision fitting'
    },
    {
      title: 'Shaft Damage',
      description: 'Worn journals, keyway damage, and dimensional loss affecting fit and function.',
      causes: ['Normal wear', 'Corrosion', 'Fatigue', 'Improper installation'],
      repair: 'Metal rebuilding to restore original dimensions'
    },
    {
      title: 'Housing & Casing Damage',
      description: 'Cracks, erosion, and wear in pump bodies, gearboxes, and equipment housings.',
      causes: ['Cavitation', 'Corrosion', 'Impact', 'Thermal stress'],
      repair: 'Crack repair and surface rebuilding composites'
    },
    {
      title: 'Seal & Gasket Failures',
      description: 'Leaking seals causing product loss, contamination, and safety hazards.',
      causes: ['Worn sealing surfaces', 'Chemical attack', 'Temperature', 'Age'],
      repair: 'Surface restoration for proper seal contact'
    }
  ];

  const repairBenefits = [
    { icon: Clock, title: 'Fast Turnaround', description: 'Many repairs completed in hours, not days' },
    { icon: DollarSign, title: 'Cost Effective', description: '50-80% less than new equipment' },
    { icon: TrendingUp, title: 'Extended Life', description: 'Often outlasts original components' },
    { icon: Settings, title: 'In-Situ Repairs', description: 'No need to remove equipment' }
  ];

  const equipmentTypes = [
    'Pumps & Compressors',
    'Fans & Blowers',
    'Gearboxes & Drives',
    'Engines & Turbines',
    'Valves & Actuators',
    'Heat Exchangers',
    'Conveyors',
    'Agitators & Mixers'
  ];

  const preventionTips = [
    'Implement regular inspection schedules',
    'Monitor vibration and temperature trends',
    'Maintain proper lubrication programs',
    'Address minor issues before they escalate',
    'Keep equipment alignment within specs',
    'Train operators on early warning signs'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Equipment Failure Prevention" description="Prevent equipment failures with proactive coating and repair solutions for industrial assets." path="/research/equipment-failures" />
      
      <PageHero
        badge="Research & Learn"
        title="Equipment Failures"
        description="Equipment failures cost industries billions annually in lost production, repairs, and replacements. Learn how to prevent failures and restore equipment when they occur."
        backgroundKey="equipment-failures"
        buttonText="Get Equipment Assessment"
      />

      {/* Failure Modes */}
      {/* Failure Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Common Equipment Failures</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {failureTypes.map((type, index) => (
              <Card key={index} className="border-l-4 border-l-red-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertOctagon className="text-red-600 mr-3" size={24} />
                    {type.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Common Causes:</p>
                    <div className="flex flex-wrap gap-2">
                      {type.causes.map((cause, idx) => (
                        <Badge key={idx} variant="outline" className="text-gray-600">{cause}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-sm"><span className="font-semibold text-green-700">Our Repair:</span> {type.repair}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Repair Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Repair vs Replace?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {repairBenefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <IconComponent className="mx-auto text-green-700 mb-4" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equipment We Repair */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Equipment We Repair</h2>
              <p className="text-gray-600 mb-8">
                Our repair solutions work on virtually any industrial equipment. 
                Whether it's rotating machinery, static equipment, or structural components, 
                we have the expertise and materials to restore function.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {equipmentTypes.map((equipment, index) => (
                  <div key={index} className="flex items-center">
                    <Cog className="text-blue-900 mr-2 flex-shrink-0" size={18} />
                    <span className="text-gray-700">{equipment}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Wrench className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Cold Repair Technology</h3>
              <p className="text-blue-100 mb-6">
                Our advanced composite repair systems allow equipment restoration without hot work. 
                Repairs can often be made in-place, eliminating costly removal and reinstallation.
              </p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/equipment-repair')}>
                View Repair Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention Tips */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Failure Prevention Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {preventionTips.map((tip, index) => (
              <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">{tip}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" className="border-green-700 text-green-700 hover:bg-green-50" onClick={() => navigate('/maintenance/smart-program')}>
              Learn About Preventative Maintenance <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-red-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <AlertOctagon className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Equipment Down? We Can Help</h2>
          <p className="text-red-100 mb-8">
            Our emergency response team provides rapid repair solutions to get your equipment back online fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-red-700 hover:bg-gray-100">
              <a href="tel:9493719948" className="flex items-center">
                Emergency: (949) 371-9948
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-red-800" onClick={() => navigate('/#contact')}>
              Request Assessment
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EquipmentFailures;
